import JSONRPC from './JSONRPC';
import offlineActions from 'js/redux/reducers/Offline';
import type { JsonRpcTransport } from './types/JSONRPCAPI';
import type {
  OfflineAccessTokenData,
  OfflineAccessTimeRemaining,
  OfflineBackendResponse,
  OfflineCommandResponse,
  OfflineInfo,
  OfflineQtObject,
  OfflineStore,
} from './types/Offline';

export type {
  OfflineAccessTokenData,
  OfflineAccessTimeRemaining,
  OfflineBackendResponse,
  OfflineCommandResponse,
  OfflineInfo,
  OfflineQtObject,
  OfflineStore,
} from './types/Offline';

function createNullJsonRpc(): JsonRpcTransport {
  return {
    request: <T = unknown>() => Promise.resolve(null as T),
    handleResponse: () => undefined,
  };
}

export class OfflineAPI {
  private jsonrpc: JsonRpcTransport;

  // dummy dispatcher that resolves all calls to null
  constructor() {
    // dummy dispatcher that resolves all calls to null
    this.jsonrpc = createNullJsonRpc();
  }

  connect(qtobject: OfflineQtObject | null, store: OfflineStore): void {
    // connecting all machine actions here
    try {
      if (qtobject) {
        qtobject.online_mode_available.connect((bool: boolean) => {
          store.dispatch(offlineActions.setOnlineAvailable(bool));
        });
        qtobject.order_sync_succeeded.connect((bool: boolean) => {
          store.dispatch(offlineActions.setOrdersSynced(bool));
        });

        qtobject.offline_mode_available.connect((bool: boolean) => {
          store.dispatch(offlineActions.setOfflineAvailable(bool));
        });
        qtobject.force_check_finished.connect(() => {
          store.dispatch(offlineActions.forceCheckFinished());
        });

        this.jsonrpc = new JSONRPC((message) =>
          qtobject.dispatch(JSON.stringify(message))
        );
        qtobject.response.connect((str: string) =>
          this.jsonrpc.handleResponse(JSON.parse(str))
        );
      }
    } catch (e) {
      console.log(e);
      this.jsonrpc = createNullJsonRpc();
    }
  }

  getOfflineAccessToken(): Promise<string | null> {
    return this.jsonrpc.request<string | null>('get_offline_access_token');
  }

  getOfflineTimeRemaining(): Promise<OfflineAccessTimeRemaining | null> {
    return this.jsonrpc.request<OfflineAccessTimeRemaining | null>(
      'get_offline_access_time_remaining'
    );
  }

  createOfflineAccessToken(
    data: OfflineAccessTokenData
  ): Promise<string | null> {
    return this.jsonrpc.request<string | null>('set_offline_access_token', [
      data,
    ]);
  }

  getCloudServerAddress(): Promise<string | null> {
    return this.jsonrpc.request<string | null>('get_cloud_server_address');
  }

  start_fall_backend(): Promise<OfflineBackendResponse> {
    return this.jsonrpc.request<OfflineBackendResponse>('start_fall_backend');
  }

  stop_fall_backend(): Promise<OfflineBackendResponse> {
    return this.jsonrpc.request<OfflineBackendResponse>('stop_fall_backend');
  }

  force_check_offline_db_updates(): Promise<OfflineCommandResponse> {
    return this.jsonrpc.request<OfflineCommandResponse>(
      'force_check_offline_db_updates'
    );
  }

  remove_offline_mode_files_and_settings(): Promise<OfflineCommandResponse> {
    return this.jsonrpc.request<OfflineCommandResponse>(
      'remove_offline_mode_files_and_settings'
    );
  }

  get_offline_info(): Promise<OfflineInfo | null> | null {
    try {
      return this.jsonrpc.request<OfflineInfo | null>('get_offline_info');
    } catch (e) {
      return null;
    }
  }
}
const api = new OfflineAPI();

export { api as default };
